import React, { useEffect, useState } from 'react';
import { Paper, Skeleton, Grid, Tooltip } from '@mui/material';
import InterpreterModeIcon from '@mui/icons-material/InterpreterMode';
import { useSelector } from 'react-redux';
import GroupIcon from '@mui/icons-material/Group';
import { useTheme } from '@mui/material/styles';
import NoDataImg from '../../../assets/images/No_data-amico.svg';
import { getGenderAgeDistribution, getUniqueWalkinWalkout } from 'api';
import { DemographicsSkeleton, InteractionsSkeleton } from 'component/SkeletonLoader';

function Rightbar() {
  const [cusAssRatioData, setCusAssRatioData] = useState(false);
  const [attended, setAttended] = useState('NA');
  const [unAttended, setUnAttended] = useState(false);
  const [interactionData, setInteractionData] = useState(false);
  const [genderData, setGenderData] = useState({ male: {}, female: {} });
  const [totalMale, setTotalMale] = useState(0);
  const [totalFemale, setTotalFemale] = useState(0);
  const [totalUnknown, setTotalUnknown] = useState(0);
  const [ageGroups, setAgeGroups] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Loading states
  const [isLoadingDemographics, setIsLoadingDemographics] = useState(true);
  const [isLoadingInteractions, setIsLoadingInteractions] = useState(true);

  const theme = useTheme();
  const date = useSelector((state) => state.customization.currentDate);
  const selectedTime = useSelector((state) => state.customization.selectedTime);
  const startTime = new Date(selectedTime.start_time);
  const endTime = new Date(selectedTime.end_time);
  const start_time = new Date(selectedTime?.start_time).toUTCString();
  const end_time = new Date(selectedTime?.end_time).toUTCString();
  const formattedStartTimeIST = startTime.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }).split(',')[1];
  const formattedEndTimeIST = endTime.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }).split(',')[1];

  const [isMounted, setIsMounted] = useState(false);

  // Color palettes for the age groups
  const maleColors = ['#A8E4B1', '#76D183', '#44BE54', '#247331', '#1A5A27'];
  const femaleColors = ['#FD9DB5', '#FC5A83', '#FB1751', '#C2043A', '#8C0328'];

  async function getCustomerAssociateRatio(commonBody, signal) {
    try {
      const data = dummydata.customerAssociateRatio;

      if (!signal.aborted) {
        if (!data || data.length === 0) {
          setCusAssRatioData(false);
        } else if (data && data.length > 0) {
          const countData = data;
          setCusAssRatioData(countData);
        }
      }
    } catch (error) {
      if (!signal.aborted) {
        setCusAssRatioData(false);
        console.log(error);
      }
    }
  }

  // const source = JSON.parse(localStorage.getItem('source'));
  const source = "trends";

  async function getAgeGenderRatio(commonBody, signal) {
    setIsLoadingDemographics(true);
    try {
      const requestData = { date: commonBody, source_id: source, start_time: start_time, end_time: end_time };
      const response = await getGenderAgeDistribution(requestData);
      const apiData = response?.data?.data[0]?.result || {};

      if (!signal.aborted) {
        if (Object.keys(apiData).length === 0) {
          setGenderData({ male: {}, female: {} });
          setTotalMale(0);
          setTotalFemale(0);
          setTotalUnknown(0);
          setAgeGroups([]);
        } else {
          // Process the API data to extract age groups and counts
          const processedData = {
            male: apiData.male || {},
            female: apiData.female || {}
          };

          // Extract all unique age groups from both male and female data
          const allAgeGroups = new Set([...Object.keys(processedData.male), ...Object.keys(processedData.female)]);

          // Convert Set to array and sort if needed
          const sortedAgeGroups = Array.from(allAgeGroups).sort();

          // Calculate totals
          const maleTotal = Object.values(processedData.male).reduce((sum, count) => sum + count, 0);
          const femaleTotal = Object.values(processedData.female).reduce((sum, count) => sum + count, 0);
          const unknownTotal = apiData.unknown?.unknown || 0;

          setGenderData(processedData);
          setTotalMale(maleTotal);
          setTotalFemale(femaleTotal);
          setTotalUnknown(unknownTotal);
          setAgeGroups(sortedAgeGroups);
        }
        setIsLoadingDemographics(false);
      }
    } catch (error) {
      if (!signal.aborted) {
        setGenderData({ male: {}, female: {}, unknown: {} });
        setTotalMale(0);
        setTotalFemale(0);
        setTotalUnknown(0);
        setAgeGroups([]);
        setIsLoadingDemographics(false);
        console.log('Error processing gender-age data:', error);
      }
    }
  }

  async function getDepartments(signal) {
    try {
      const response = await fetch('https://disha-pocket.neophyte.live/trends-api/api/sentinel/getDepartments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_id: source
        }),
        signal: signal
      });
      const data = await response.json();
      if (!signal.aborted && data.status === 200) {
        setDepartments(data.data);
      }
    } catch (error) {
      if (!signal.aborted) {
        console.error('Error fetching departments:', error);
      }
    }
  }

  async function getInteractionNumbers(commonBody, signal) {
    setIsLoadingInteractions(true);
    try {
      const requestData = { date: commonBody, source_id: source, start_time: start_time, end_time: end_time };
      const response = await getUniqueWalkinWalkout(requestData);
      const data = response.data.data[0]?.footfall;

      if (!signal.aborted) {
        if (!data || !data.zoneFootfall || data.zoneFootfall.length === 0) {
          setAttended(0);
          setUnAttended(0);
          setInteractionData([]);
        } else {
          const totalAttended = data.total_attended;
          const totalUnAttended = data.total_unattended;
          const zoneWise = data.zoneFootfall.map((zone) => ({
            _id: zone.zone_name,
            footfall: zone.footfall,
            interacted: zone.footfall === 0 ? 0 : zone.attended,
            not_interacted: zone.footfall === 0 ? 0 : zone.footfall - zone.attended
          }));

          setAttended(totalAttended);
          setUnAttended(totalUnAttended);
          setInteractionData(zoneWise);
        }
        setIsLoadingInteractions(false);
      }
    } catch (error) {
      if (!signal.aborted) {
        setAttended(0);
        setUnAttended(0);
        setInteractionData([]);
        setIsLoadingInteractions(false);
        console.log('Error fetching interaction numbers:', error);
      }
    }
  }

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    if (isMounted) {
      const commonBody = {
        start_time: formattedStartTimeIST,
        end_time: formattedEndTimeIST,
        start_date: date,
        storeId: '65c5e26a0b5be5dc7af327dc'
      };

      const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      const formattedDate = formatDate(date);

      getCustomerAssociateRatio(commonBody, signal);
      getInteractionNumbers(formattedDate, signal);
      getAgeGenderRatio(formattedDate, signal);
      getDepartments(signal);
    }

    return () => {
      abortController.abort();
    };
  }, [date, isMounted, formattedStartTimeIST, formattedEndTimeIST, start_time, end_time]);

  cusAssRatioData && cusAssRatioData.sort((a, b) => b.customer_count / b.employee_count - a.customer_count / a.employee_count);
  interactionData && interactionData.sort((a, b) => a.interacted - b.interacted);

  const paperStyle = {
    width: '100%',
    maxHeight: '280px',
    height: '280px',
    padding: '10px',
    marginBottom: '11.5px',
    position: 'relative',
    left: 'auto',
    borderRadius: '15px',
    display: 'flex',
    alignItems: 'center',
    flex: '1'
  };

  const paperStyleLastCard = {
    width: '100%',
    maxHeight: '360px',
    height: '360px',
    padding: '10px',
    marginBottom: '11.5px',
    position: 'relative',
    left: 'auto',
    borderRadius: '15px',
    display: 'flex',
    flex: '1'
  };

  return (
    <>
      <Grid
        item
        xs={12}
        sx={{
          width: '100%',
          mb: -1,
          mt: 1,
          paddingTop: '8px',
          [theme.breakpoints.up('sm')]: {
            mb: -1,
            mt: 1
          },
          [theme.breakpoints.up('md')]: {
            mb: -1,
            mt: -1
          }
        }}
      >
        {/* Demographics Analytics Card */}
        <Paper elevation={3} sx={paperStyle}>
          {isLoadingDemographics ? (
            <DemographicsSkeleton />
          ) : ageGroups.length > 0 ? (
            <div className="flex w-full flex-col gap-1">
              <div className="flex items-center justify-center gap-2 w-full">
                <GroupIcon className="bg-[#444444] text-white rounded-full p-2 text-5xl" />
                <div className="w-full">
                  <p className="text-xl">Demographics Analytics</p>
                </div>
              </div>
              <div className="bg-slate-100 flex-grow overflow-y-auto h-[207px] p-2 rounded-lg scrollbar">
                {/* Male Section */}
                <p className="text-lg font-semibold text-[#44BE54] mb-1">Male: {totalMale}</p>
                <div className="flex w-full h-4 bg-gray-300 rounded-full overflow-hidden">
                  {ageGroups.map((ageGroup, index) => {
                    const ageValue = genderData.male[ageGroup] || 0;
                    const width = totalMale ? (ageValue / totalMale) * 100 : 0;
                    const colorIndex = index % maleColors.length;
                    return (
                      <Tooltip
                        key={`male-${ageGroup}`}
                        title={<span className="text-lg p-2">{`${ageGroup}: ${genderData.male[ageGroup] || 0}`}</span>}
                        arrow
                        PopperProps={{
                          modifiers: [{ name: 'preventOverflow', enabled: true }]
                        }}
                        sx={{ fontSize: '1.2rem', padding: '8px' }}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: `${width}%`,
                            backgroundColor: maleColors[colorIndex]
                          }}
                        ></div>
                      </Tooltip>
                    );
                  })}
                </div>

                {/* Male Legend */}
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {ageGroups.map((ageGroup, index) => {
                    const colorIndex = index % maleColors.length;
                    return (
                      <div key={`male-legend-${ageGroup}`} className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: maleColors[colorIndex] }}></div>
                        <p className="text-xs">{ageGroup}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Female Section */}
                <p className="text-lg font-semibold text-[#FB1751] mt-3 mb-1">Female: {totalFemale}</p>
                <div className="flex w-full h-4 bg-gray-300 rounded-full overflow-hidden">
                  {ageGroups.map((ageGroup, index) => {
                    const ageValue = genderData.female[ageGroup] || 0;
                    const width = totalFemale ? (ageValue / totalFemale) * 100 : 0;
                    const colorIndex = index % femaleColors.length;
                    return (
                      <Tooltip
                        key={`female-${ageGroup}`}
                        title={<span className="text-lg p-2">{`${ageGroup}: ${genderData.female[ageGroup] || 0}`}</span>}
                        arrow
                        PopperProps={{
                          modifiers: [{ name: 'preventOverflow', enabled: true }]
                        }}
                        sx={{ fontSize: '1.2rem', padding: '8px' }}
                      >
                        <div
                          className="h-full"
                          style={{
                            width: `${width}%`,
                            backgroundColor: femaleColors[colorIndex]
                          }}
                        ></div>
                      </Tooltip>
                    );
                  })}
                </div>

                {/* Female Legend */}
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {ageGroups.map((ageGroup, index) => {
                    const colorIndex = index % femaleColors.length;
                    return (
                      <div key={`female-legend-${ageGroup}`} className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: femaleColors[colorIndex] }}></div>
                        <p className="text-xs">{ageGroup}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Unknown Section */}
                {totalUnknown > 0 && (
                  <>
                    <p className="text-lg font-semibold text-[#7C7C7C] mt-3 mb-1">Unknown: {totalUnknown}</p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <img className="w-80 h-80 ml-10 mb-10" src={NoDataImg} alt="No data" />
          )}
        </Paper>

        {/* Interactions Card - Remains the same as before */}
        <Paper elevation={3} sx={paperStyleLastCard}>
          {isLoadingInteractions ? (
            <InteractionsSkeleton />
          ) : interactionData && interactionData.length > 0 ? (
            <div className="flex  w-full  flex-col gap-1">
              <div className="flex items-center justify-center gap-2 w-full">
                {attended != 'NA' ? (
                  <InterpreterModeIcon className="bg-[#444444] text-white rounded-full p-2 text-5xl" />
                ) : (
                  <Skeleton variant="circular" width={60} height={45} />
                )}
                <div className="w-full">
                  {attended != 'NA' ? (
                    <div className="w-full flex space-x-1">
                      <Tooltip title={`${attended} customers`} arrow placement="top">
                        <div className="w-1/2 flex flex-col text-center  rounded-md shadow-md bg-[#44BE54] drop-shadow-md text-white">
                          <span>Attended</span>
                          <span className="text-2xl">{((attended / (attended + unAttended)) * 100).toFixed(1)}%</span>
                        </div>
                      </Tooltip>
                      <Tooltip title={`${unAttended} customers`} arrow placement="top">
                        <div className="w-1/2 flex flex-col text-center bg-[#FB1751] rounded-md shadow-md drop-shadow-md text-white">
                          <span>Unattended</span>
                          <span className="text-2xl">{((unAttended / (attended + unAttended)) * 100).toFixed(1)}%</span>
                        </div>
                      </Tooltip>
                    </div>
                  ) : (
                    <Skeleton variant="rectangular" className="mb-3 rounded-sm" width={50} height={20} />
                  )}

                  {attended != 'NA' ? (
                    <p className="text-lg">Interactions</p>
                  ) : (
                    <Skeleton variant="rectangular" width={150} height={15} className=" mb-2 rounded-sm" />
                  )}
                </div>
              </div>
              {attended != 'NA' ? (
                <div className=" bg-slate-100 flex-grow overflow-y-auto h-[184px] p-2 rounded-lg scrollbar">
                  {interactionData.map((item, index) => {
                    const department = departments.find((dept) => dept.name === item._id);
                    const zoneColor = department ? department.color : '#2D9BBC';
                    return (
                      <div className="mt-2" key={index}>
                        <div className="flex gap-1 items-center">
                          <div
                            className=" rounded-full h-4 w-4"
                            style={{
                              backgroundColor: zoneColor
                            }}
                          >
                            {' '}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="mt-0.5">{item._id} : </span>
                            <Tooltip title={`${item.interacted} visits`} arrow placement="top">
                              <div className="h-6  text-center px-2 border border-[#44BE54] shadow-md rounded-md text-[#44BE54] drop-shadow-md">
                                {((item.interacted / (item.interacted + item.not_interacted)) * 100).toFixed(1)}%
                              </div>
                            </Tooltip>
                            {item.not_interacted > 0 && (
                              <Tooltip title={`${item.not_interacted} visits`} arrow placement="top">
                                <div className="h-6  border border-[#FB1751] text-[#FB1751] text-center px-2 shadow-md rounded-md drop-shadow-md">
                                  {((item.not_interacted / (item.interacted + item.not_interacted)) * 100).toFixed(1)}%
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Skeleton variant="rectangular" height={184} className="rounded-md" />
              )}
            </div>
          ) : (
            <img className="w-80 h-80 ml-10 mb-10" src={NoDataImg} alt="No data" />
          )}
        </Paper>
      </Grid>
    </>
  );
}

export default Rightbar;
